import React, { useState, useEffect, useMemo } from 'react';
import { Toast, useToast } from '@chakra-ui/react';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Button,
  Flex,
  Spinner,
  Text,
  Select,
  IconButton,
  Input,
} from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import TagModalComponent from '../components/tagmodal.component';
import RWModalComponent from '../components/rwmodal.component';
import DeleteTagModalComponent from '../components/deletetagmodal.component';
import { useQuery, useQueryClient } from 'react-query';
import { GetAllTags, exportTagsToExcel } from '../api/tag';
import { unitOptions, tagType } from '../utils/constants';
import ImportTagExcelModalComponent from '../components/ImportTagExcelModalComponent';
import { saveAs } from 'file-saver';
import { ta } from 'date-fns/locale';
import { useTagSignalR } from '../hooks/useTagSignalR';

const TagList = () => {
  useTagSignalR(); // ✅ SignalR'ı başlat

  const queryClient = useQueryClient();
  const [selectedTag, setSelectedTag] = useState(null);
  const [deviceOptions, setDeviceOptions] = useState([]);
  const [isRWModalOpen, setRWModalOpen] = useState(false);
  const [isTagModalOpen, setTagModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isImportModalOpen, setImportModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [searchTerm, setSearchTerm] = useState('');
  const toast = useToast();
  const fetchTags = async () => {
    const data = await GetAllTags();

    // Tags'leri name alanına göre sıralama
    const sortedData = data.map(device => ({
      ...device,
      tags: device.tags
        .filter(
          tag => tag && typeof tag.name === 'string' && tag.name.trim() !== ''
        ), // undefined veya boş olanları filtrele
    }));

    return sortedData;
  };

  const {
    data: tagsData,
    isLoading: isTagsLoading,
    error: tagsError,
  } = useQuery('tags', fetchTags, {
    refetchOnWindowFocus: false, // ✅ Pencere odak değiştirildiğinde tekrar çağrılmasın
    refetchInterval: false, // ✅ Belirli aralıklarla tekrar çağrılmasın
  });

  // SignalR veya ilk fetch sonrası tagsData değiştiğinde deviceOptions'ı güncelle
  useEffect(() => {
    if (tagsData) {
      setDeviceOptions(tagsData);
    }
  }, [tagsData]);

  if (isTagsLoading) {
    return (
      <Box
        w="100%"
        h="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Spinner />
      </Box>
    );
  }

  if (tagsError) {
    return <div>Error fetching data</div>;
  }

  const allTags = deviceOptions.flatMap(device =>
    device.tags.map(tag => ({ ...tag, _deviceName: device.deviceName }))
  );

  const filteredTags = searchTerm
    ? allTags.filter(
        tag =>
          tag.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          tag._deviceName?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : allTags;

  const totalPages = Math.ceil(filteredTags.length / pageSize);
  const paginatedTags = filteredTags.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const getModifyStatus = tag => {
    if (tag.function === 'RO') {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <span style={{ color: 'gray' }}>-</span>
        </div>
      );
    } else if (tag.function === 'RW') {
      let colorScheme = 'gray';
      let isClickable = true;

      if (tag.isModify === '1') {
        colorScheme = 'green';
      } else if (tag.isModify === '2' || tag.isModify === '0') {
        colorScheme = 'red';
      }

      const displayValue = tag.modifyValue === '' ? 'n.a.' : tag.modifyValue;

      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          {isClickable ? (
            <Button
              colorScheme={colorScheme}
              variant="link"
              style={{ cursor: 'pointer' }}
              onClick={() => handleModifyClick(tag)}
            >
              {displayValue}
            </Button>
          ) : (
            <span>{displayValue}</span>
          )}
        </div>
      );
    } else {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <span>-</span>
        </div>
      );
    }
  };

  const handleModifyClick = tag => {
    if (tag.function === 'RW') {
      setSelectedTag(tag);
      setRWModalOpen(true);
    }
  };

  const handleAddTag = () => {
    setSelectedTag(null);
    setTagModalOpen(true);
  };

  const handleEditTag = tag => {
    const fullTag = {
      ...tag,
      function: tag.function,
      homePage: tag.homePage,
    };
    setSelectedTag(fullTag);
    setTagModalOpen(true);
  };

  const handleDeleteTag = tag => {
    setSelectedTag(tag);
    setIsDeleteModalOpen(true);
  };

  const handleModalSuccess = () => {
    queryClient.invalidateQueries('tags');
  };

  const handleExportTags = async () => {
    try {
      const data = await exportTagsToExcel();
      const blob = new Blob([data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      saveAs(blob, 'tags_export.xlsx');
    } catch (error) {
      console.error('Error exporting tags:', error);
      Toast({
        title: 'Error',
        description: 'Failed to export tags.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };
  return (
    <Box w="100%" minH="70vh" display="flex" flexDirection="column" p={5}>
      <Flex justifyContent="space-between" alignItems="center" mb={5}>
        <Heading size="md">Tag List</Heading>
        <Input
          placeholder="Search by name or device..."
          value={searchTerm}
          onChange={e => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          maxW="300px"
          size="sm"
        />
      </Flex>

      <Box flex="1" overflowY="auto" maxHeight="70vh">
        <TableContainer overflowY="auto" maxHeight="70vh">
          <Table variant="simple" colorScheme="gray" size="sm">
            <Thead position="sticky" top={0} zIndex={1} bg="white">
              <Tr>
                <Th px={4} py={2}>
                  Name
                </Th>
                <Th px={4} py={2}>
                  Device
                </Th>
                <Th px={4} py={2}>
                  Type
                </Th>
                <Th px={4} py={2}>
                  Address
                </Th>
                <Th px={4} py={2}>
                  Value
                </Th>
                <Th px={4} py={2}>
                  Unit
                </Th>
                <Th px={4} py={2}>
                  Modify
                </Th>
                <Th px={4} py={2}>
                  Action
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {paginatedTags.map(tag => (
                  <Tr key={tag.registerId}>
                    <Td px={4} py={2}>
                      {tag.name}
                    </Td>
                    <Td px={4} py={2}>
                      {tag._deviceName}
                    </Td>
                    <Td px={4} py={2}>
                      {tagType.find(type => type.id === tag.type)?.label}
                    </Td>
                    <Td px={4} py={2}>
                      {tag.address}
                    </Td>
                    <Td px={4} py={2}>
                      {typeof tag.value === 'number'
                        ? Number(tag.value).toFixed(tag.value % 1 === 0 ? 0 : 2)
                        : tag.value}
                    </Td>
                    <Td px={4} py={2}>
                      {unitOptions.find(unit => unit.id === tag.unit)?.label}
                    </Td>
                    <Td px={4} py={2}>
                      {getModifyStatus(tag)}
                    </Td>
                    <Td px={4} py={2}>
                      <Flex align="center">
                        <Text
                          as="span"
                          mr={2}
                          cursor="pointer"
                          onClick={() => handleEditTag(tag)}
                        >
                          edit
                        </Text>
                        <Text as="span" mx={2}>
                          |
                        </Text>
                        <Text
                          as="span"
                          cursor="pointer"
                          onClick={() => handleDeleteTag(tag)}
                        >
                          delete
                        </Text>
                      </Flex>
                    </Td>
                  </Tr>
                ))}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>

      <Flex justifyContent="space-between" alignItems="center" mt={4}>
        <Flex alignItems="center" gap={2}>
          <Text fontSize="sm" whiteSpace="nowrap">
            {filteredTags.length} tag
          </Text>
          <Select
            size="sm"
            w="80px"
            value={pageSize}
            onChange={e => {
              setPageSize(Number(e.target.value));
              setCurrentPage(1);
            }}
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={200}>200</option>
          </Select>
          <IconButton
            icon={<ChevronLeftIcon />}
            size="sm"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            isDisabled={currentPage === 1}
            aria-label="Previous page"
          />
          <Text fontSize="sm" whiteSpace="nowrap">
            {currentPage} / {totalPages || 1}
          </Text>
          <IconButton
            icon={<ChevronRightIcon />}
            size="sm"
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            isDisabled={currentPage >= totalPages}
            aria-label="Next page"
          />
        </Flex>
        <Flex gap={2}>
          <Button size="sm">Transfer</Button>
          <Button size="sm" onClick={handleExportTags}>
            Export
          </Button>
          <Button size="sm" onClick={() => setImportModalOpen(true)}>
            Import
          </Button>
          <Button size="sm" onClick={handleAddTag}>Add Tag</Button>
        </Flex>
      </Flex>

      <TagModalComponent
        isOpen={isTagModalOpen}
        onClose={() => {
          setTagModalOpen(false);
          setSelectedTag(null);
          handleModalSuccess();
        }}
        tag={selectedTag}
        deviceOptions={deviceOptions}
        onSuccess={handleModalSuccess}
      />

      <RWModalComponent
        isOpen={isRWModalOpen}
        onClose={() => {
          setRWModalOpen(false);
          setSelectedTag(null);
          handleModalSuccess();
        }}
        tag={selectedTag}
        deviceOptions={deviceOptions}
        onSuccess={handleModalSuccess}
      />

      {isDeleteModalOpen && (
        <DeleteTagModalComponent
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setSelectedTag(null);
            handleModalSuccess();
          }}
          tag={selectedTag}
          deviceOptions={deviceOptions}
          onSuccess={handleModalSuccess}
        />
      )}
      <ImportTagExcelModalComponent
        isOpen={isImportModalOpen}
        onClose={() => setImportModalOpen(false)}
        onSuccess={handleModalSuccess}
        deviceId="00001791-9eca-46af-9cae-8e0bd1759d42" // Cihaz ID'si sabit
      />
    </Box>
  );
};

export default TagList;
